import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ExternalLink, LifeBuoy, MapPin, Mail, AlertCircle } from 'lucide-react';
import { crisisResources, localResources, organizationInfo } from '../mockData';

const ResourceCard = ({ item, testid }) => {
  const isExternal = item.href.startsWith('http');
  const Icon = item.href.startsWith('tel:')
    ? Phone
    : item.href.startsWith('sms:')
    ? MessageCircle
    : item.href.startsWith('mailto:')
    ? Mail
    : ExternalLink;

  return (
    <div data-testid={testid} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col">
      <h3 className="service-card-title mb-2">{item.title}</h3>
      <p className="service-card-description mb-5 flex-1">{item.description}</p>
      <a
        href={item.href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="btn-secondary flex items-center gap-2 justify-center"
      >
        <Icon className="h-4 w-4" />
        {item.action}
      </a>
    </div>
  );
};

const ResourcesPage = () => {
  return (
    <div className="pt-20">
      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Resources & Support</h1>
          <p className="body-large max-w-3xl mx-auto">
            You don't have to wait — and you don't have to do this alone. While we build our local programs,
            here are trusted resources you can reach out to right now for help, hope, and connection.
          </p>
        </div>
      </section>

      {/* Crisis banner */}
      <section className="py-8 bg-red-50 border-l-4 border-red-500">
        <div className="container">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-red-800 mb-2">If you're in crisis, please reach out now</h3>
              <p className="text-red-700 mb-4">
                If you or someone you know is in immediate danger or having thoughts of self-harm,
                please call <strong>911</strong> or contact the resources below right away. You matter, and help is available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis resources */}
      <section className="py-16">
        <div className="container">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <LifeBuoy className="h-7 w-7 text-green-600" />
            <h2 className="heading-2">24/7 Crisis & Immediate Support</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {crisisResources.map((item, i) => (
              <ResourceCard key={i} item={item} testid={`crisis-resource-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Local resources */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="flex items-center gap-3 mb-4 justify-center">
            <MapPin className="h-7 w-7 text-green-600" />
            <h2 className="heading-2">Local & Statewide Help</h2>
          </div>
          <p className="body-large text-center max-w-2xl mx-auto mb-10">
            Trusted Michigan resources serving Manistee and Mason Counties and beyond.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {localResources.map((item, i) => (
              <ResourceCard key={i} item={item} testid={`local-resource-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center bg-green-50 p-8 rounded-lg">
            <h3 className="heading-3 mb-4">Have a question or need a hand finding help?</h3>
            <p className="body-medium mb-6">
              Reach out any time. We're happy to listen and help point you toward the right support
              in our community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`mailto:${organizationInfo.email}`} className="btn-primary flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email {organizationInfo.email}
              </a>
              <Link to="/contact" className="btn-secondary">
                Send Us a Message
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
